import { Particles } from '@/components/effects';
import { BlurFade } from '@/components/effects';
import { ChevronDown } from 'lucide-react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyMedia, MediaResponse } from '@/lib/collections/media';

const LOGO_URL =
  'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69c60fc6dcc9e2093702872d';

export const DangerHero: React.FC = () => {
  const { data: mediaEntries } = useRealtimeData<MediaResponse[]>(
    subscribeManyMedia,
    true,
  );

  const heroVideoEntry = (mediaEntries ?? []).find(m => m.type === 'hero-video') ?? null;
  const heroVideoUrl = heroVideoEntry?.url ?? null;

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background fill — always visible as fallback behind video */}
      <div className="absolute inset-0" style={{ background: '#080d1f', zIndex: 0 }} />

      {/* Video background — native MP4 for all screen sizes */}
      {heroVideoUrl && (
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden block"
          style={{ zIndex: 1 }}
        >
          <video
            key={heroVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '120%',
              height: '120%',
              transform: 'translate(-50%, -50%)',
              objectFit: 'cover',
              pointerEvents: 'none',
            }}
          >
            <source src={heroVideoUrl} type="video/mp4" />
          </video>
        </div>
      )}


      {/* Dark overlay — sits on top of video, behind all content */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2,
        }}
      />

      {/* Radial vignette gradient from center */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(124,252,0,0.07) 0%, rgba(230,57,70,0.04) 40%, transparent 70%)',
          zIndex: 3,
        }}
      />

      {/* Top cinematic bar */}
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #E63946 0%, #7CFC00 50%, #E63946 100%)', zIndex: 3 }} />

      {/* Floating particles */}
      <div className="absolute inset-0" style={{ zIndex: 3 }}>
        <Particles quantity={60} color="hsl(90 100% 50%)" />
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center text-center px-6 py-20" style={{ zIndex: 4 }}>
        {/* Logo — the hero centerpiece */}
        <BlurFade delay={0.15}>
          <div className="relative mb-8">
            {/* Glow halo behind logo */}
            <div
              className="absolute inset-0 blur-3xl rounded-full scale-75 opacity-40 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse, #7CFC00 0%, #E63946 50%, transparent 80%)' }}
            />
            {/* Glitch wrapper — pseudo-elements use --logo-src CSS var for the RGB split clones */}
            <div
              className="logo-glitch-wrapper"
              style={{ '--logo-src': `url(${LOGO_URL})` } as React.CSSProperties}
            >
              <img
                src={LOGO_URL}
                alt="Danger Dan! Logo"
                className="relative w-[280px] md:w-[840px] lg:w-[980px] max-w-full drop-shadow-2xl"
                style={{
                  filter: 'drop-shadow(0 0 40px rgba(124,252,0,0.35)) drop-shadow(0 0 80px rgba(230,57,70,0.2))',
                }}
              />
            </div>
          </div>
        </BlurFade>


      </div>

      {/* Bouncing scroll indicator — mobile size */}
      <div
        className="absolute left-0 right-0 flex justify-center pointer-events-none block md:hidden"
        style={{
          bottom: '60px',
          zIndex: 5,
          animation: 'bounce 2s infinite',
        }}
      >
        <ChevronDown size={40} className="text-sky-400 opacity-50" strokeWidth={1.5} />
      </div>
      {/* Bouncing scroll indicator — desktop size */}
      <div
        className="absolute left-0 right-0 flex justify-center pointer-events-none hidden md:flex"
        style={{
          bottom: '120px',
          zIndex: 5,
          animation: 'bounce 2s infinite',
        }}
      >
        <ChevronDown size={112} className="text-sky-400 opacity-50" strokeWidth={1.5} />
      </div>

      {/* Bottom cinematic bar */}
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(124,252,0,0.3) 50%, transparent 100%)', zIndex: 3 }} />

      {/* Scroll indicator — subtle gradient fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(8,13,31,0.9))', zIndex: 5 }} />
    </section>
  );
};

export default DangerHero;
